import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateImportAction,
    CertificateStore,
    X509CertificateDetails
} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useNavigate} from "react-router-dom";

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CertificateImportDialogProps {
    certificateStore : CertificateStore
    onClose : OnClose
}

/*
 * REST api for accessing the back-end
 */
const certificateApi = new CertificateApi(ApiConfig.config());

export const CertificateImportDialog = ({certificateStore, onClose} : CertificateImportDialogProps) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The file to import
     */
    const [file, setFile] = useState<File>();
    /**
     * Import action (ImportAll, SkipSelfSigned, MustBeSelfSigned)
     */
    const [importAction, setImportAction] = useState<CertificateImportAction>(
        certificateStore == "ROOTS" ? CertificateImportAction.MustBeSelfSigned : CertificateImportAction.SkipSelfSigned);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while importing to show a spinner on the import button
     */
    const [importing, setImporting] = useState<boolean>(false);
    /**
     * Ref to the file input field
     */
    const fileInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const importCertificates = () =>
    {
        setImporting(true);

        certificateApi.certificateControllerImportCertificates(certificateStore, importAction, file)
            .then((response) =>
            {
                const imported : X509CertificateDetails[] = response.data;

                if (imported.length > 0) {
                    onClose(DialogCloseCause.Succes);
                }
                else {
                    setErrorMessage(t("certificates.no-new-certificates-imported"));
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error importing certificates", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setImporting(false);
            });
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               onEntered={() => fileInputRef.current?.focus()}
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("certificates.import-certificates-from", {store: certificateStore})} </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Form.Group className="mb-3">
                    <Form.Label>{t("certificates.certificate-file")}</Form.Label>
                    <Form.Control
                        size="sm"
                        type="file"
                        accept=""
                        ref={fileInputRef}
                        onChange={e => setFile(((e.target as HTMLInputElement).files as FileList)[0])}
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="certificate-import-action">
                    <Form.Label>{t("certificates.import-action")}</Form.Label>
                    <Form.Select value={importAction}
                                 onChange={(e) => setImportAction(Utils.stringToEnum(CertificateImportAction, e.target.value as CertificateImportAction))}>
                        <option value="IMPORT_ALL">{t("certificates.import-all")}</option>
                        <option value="SKIP_SELF_SIGNED">{t("certificates.skip-self-signed")}</option>
                        <option value="MUST_BE_SELF_SIGNED">{t("certificates.import-self-signed-only")}</option>
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={importCertificates}
                        disabled={file === undefined}>
                    {importing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {importing ? t("certificates.importing") : t("certificates.import")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
